import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { DEFAULT_DATE_FORMAT } from 'api/models';
import {
  FetchableResourceType,
  PlanningLevelConfig,
  ResourceType,
} from 'api/resource-management/types';
import {
  EventResizeParams,
  ResizeEnd,
} from 'components/organisms/resource-management/scheduler-config/listeners';
import { AllocationEventModel } from 'components/organisms/resource-management/scheduler-config/models';
import { bulkActionsMapActions } from 'components/organisms/resource-management/utils/bulk-actions';
import {
  endDateParser,
  isDateBetweenRange,
  shiftDateIfOnNonWorkingDay,
  startDateParser,
} from 'components/organisms/resource-management/utils/dates';
import { asAllocationPayload } from 'components/organisms/resource-management/utils/events-conversion/common';
import { filterAllocationEvents } from 'components/organisms/resource-management/utils/events/events';
import { numberOfWorkingDaysBetweenDates } from 'components/organisms/timesheets/common/utils';
import { useAllocationAndSummaryManager } from '../../resource-summary-manager';
import { ExtendedManagerProps } from '../../types';
import { useTotalAllocationInUIManager } from '../../ui-total-allocation-manager';

type ReturnProps = (params: EventResizeParams) => void;

export const useAllocationResizeHandler = (
  props: ExtendedManagerProps,
): ReturnProps => {
  const {
    schedulerRef,
    workingDaysConfig,
    bulkUpdate,
    resourceManagementConfig,
  } = props;

  const { updateAllocationAndSummary } = useAllocationAndSummaryManager(props);
  const { updateTotalAllocation } = useTotalAllocationInUIManager(props);

  const isHoursPerWeek = useMemo(
    () =>
      resourceManagementConfig?.planningLevelConfig ===
      PlanningLevelConfig.HoursPerWeek,
    [resourceManagementConfig?.planningLevelConfig],
  );

  return useCallback(
    (resizeParam: EventResizeParams) => {
      const allocationEvent = resizeParam.event;
      const range =
        resizeParam.resizedAt === ResizeEnd.StartDate
          ? [
              shiftDateIfOnNonWorkingDay(
                moment(resizeParam.newStartDate),
                workingDaysConfig,
              ),
              moment(resizeParam.oldStartDate),
            ]
          : [
              shiftDateIfOnNonWorkingDay(
                moment(resizeParam.newEndDate),
                workingDaysConfig,
                false,
              ),
              moment(resizeParam.oldEndDate),
            ];

      const fromDate = (
        resizeParam.resizedAt === ResizeEnd.StartDate
          ? range[0].isBefore(range[1])
            ? range[0]
            : range[1]
          : moment(allocationEvent.startDate)
      ).startOf('day');
      const toDate = (
        resizeParam.resizedAt === ResizeEnd.EndDate
          ? range[0].isAfter(range[1])
            ? range[0]
            : range[1]
          : moment(allocationEvent.endDate)
      ).endOf('day');

      const checkRange = {
        fromDate,
        toDate,
      };

      let diffFromWeekSnap = 0;

      if (resizeParam.resizedAt === ResizeEnd.EndDate) {
        diffFromWeekSnap = moment(resizeParam.newEndDate).diff(
          moment(resizeParam.newEndDate).endOf('week'),
          'days',
        );
      } else {
        diffFromWeekSnap = moment(resizeParam.newStartDate).diff(
          moment(resizeParam.newStartDate).startOf('week'),
          'days',
        );
      }

      let differenceInTotalMinutes = 0;
      const oldTotalMinutes = allocationEvent.get('totalMinutes');
      const { updateAllocation, deleteAllocation, actionsMap } =
        bulkActionsMapActions();

      schedulerRef.current.instance.suspendRefresh();
      if (resizeParam.resizedAt === ResizeEnd.StartDate) {
        const newStartDate = shiftDateIfOnNonWorkingDay(
          moment(resizeParam.newStartDate),
          workingDaysConfig,
        );

        if (isHoursPerWeek) {
          const endDate = moment(allocationEvent.endDate);

          if (
            newStartDate.isSameOrAfter(endDate, 'week') ||
            diffFromWeekSnap < 4
          ) {
            allocationEvent.setStartDate(
              moment(resizeParam.newStartDate)
                .startOf('week')
                .add(1, 'day')
                .toDate(),
              false,
            );
          } else {
            allocationEvent.setStartDate(
              moment(resizeParam.newStartDate)
                .add(1, 'week')
                .startOf('week')
                .add(1, 'day')
                .toDate(),
              false,
            );
          }
        } else {
          allocationEvent.setStartDate(newStartDate.toDate(), false);
        }
      } else {
        const newEndDate = shiftDateIfOnNonWorkingDay(
          moment(resizeParam.newEndDate),
          workingDaysConfig,
          false,
        );

        if (isHoursPerWeek) {
          const startDate = moment(allocationEvent.startDate);

          if (
            newEndDate.isSameOrBefore(startDate, 'week') ||
            diffFromWeekSnap >= -3
          ) {
            allocationEvent.setEndDate(
              moment(resizeParam.newEndDate)
                .endOf('week')
                .subtract(1, 'day')
                .toDate(),
              false,
            );
          } else {
            allocationEvent.setEndDate(
              moment(resizeParam.newEndDate)
                .subtract(1, 'week')
                .endOf('week')
                .subtract(1, 'day')
                .toDate(),
              false,
            );
          }
        } else {
          allocationEvent.setEndDate(newEndDate.toDate(), false);
        }
      }

      const totalMinutes =
        numberOfWorkingDaysBetweenDates(
          moment(allocationEvent.startDate),
          moment(allocationEvent.endDate),
          workingDaysConfig,
        ) * allocationEvent.get('minutes');

      allocationEvent.set('totalMinutes', totalMinutes);
      differenceInTotalMinutes =
        differenceInTotalMinutes - oldTotalMinutes + totalMinutes;

      updateAllocation({
        ...asAllocationPayload(allocationEvent),
        startDate: moment(allocationEvent.startDate).format(
          DEFAULT_DATE_FORMAT,
        ),
        endDate: moment(allocationEvent.endDate).format(DEFAULT_DATE_FORMAT),
      });

      const allEvents: AllocationEventModel[] =
        schedulerRef.current.instance.eventStore.allRecords;
      const eventsInSameTimeline = filterAllocationEvents(allEvents, {
        fromDate: checkRange.fromDate,
        toDate: checkRange.toDate,
        resourceIdentifier: {
          ...(allocationEvent.get('userId') != null
            ? {
                userId: allocationEvent.get('userId'),
                type: ResourceType.User,
              }
            : {
                roleId: allocationEvent.get('roleId'),
                type: ResourceType.Role,
                teamId: allocationEvent.get('teamId'),
              }),
        },
      });

      eventsInSameTimeline.forEach((event) => {
        if (
          event.resourceId !== allocationEvent.resourceId ||
          event.id === allocationEvent.id
        ) {
          return;
        }
        const startDateIsBetween = isDateBetweenRange(
          startDateParser(event.startDate),
          {
            startDate: startDateParser(allocationEvent.startDate),
            endDate: endDateParser(allocationEvent.endDate),
          },
        );

        const endDateIsBetween = isDateBetweenRange(
          startDateParser(event.endDate),
          {
            startDate: startDateParser(allocationEvent.startDate),
            endDate: endDateParser(allocationEvent.endDate),
          },
        );

        if (startDateIsBetween && endDateIsBetween) {
          deleteAllocation(asAllocationPayload(event));
          event.set('minutes', 0);
          differenceInTotalMinutes -= event.get('totalMinutes');
          schedulerRef.current.instance.eventStore.remove(event);
        } else if (startDateIsBetween) {
          const eventOldTotalMinutes = event.get('totalMinutes');
          const startDate = shiftDateIfOnNonWorkingDay(
            endDateParser(allocationEvent.endDate).add(1, 'day').startOf('day'),
            workingDaysConfig,
          );
          const eventTotalMinutes =
            numberOfWorkingDaysBetweenDates(
              startDate,
              moment(event.endDate),
              workingDaysConfig,
            ) * event.get('minutes');

          event.setStartDate(startDate.toDate(), false);
          event.set('totalMinutes', eventTotalMinutes);
          differenceInTotalMinutes =
            differenceInTotalMinutes - eventOldTotalMinutes + eventTotalMinutes;
          updateAllocation({
            ...asAllocationPayload(event),
            startDate: startDate.format(DEFAULT_DATE_FORMAT),
          });
        } else if (endDateIsBetween) {
          const eventOldTotalMinutes = event.get('totalMinutes');
          const endDate = shiftDateIfOnNonWorkingDay(
            moment(allocationEvent.startDate).subtract(1, 'day').endOf('day'),
            workingDaysConfig,
            false,
          );
          const eventTotalMinutes =
            numberOfWorkingDaysBetweenDates(
              moment(event.startDate),
              endDate,
              workingDaysConfig,
            ) * event.get('minutes');

          event.setEndDate(endDate.toDate(), false);
          event.set('totalMinutes', eventTotalMinutes);
          differenceInTotalMinutes =
            differenceInTotalMinutes - eventOldTotalMinutes + eventTotalMinutes;
          updateAllocation({
            ...asAllocationPayload(event),
            endDate: endDate.format(DEFAULT_DATE_FORMAT),
          });
        }
      });

      bulkUpdate(actionsMap)
        .then(({ processSummaryUpdate, processTotalAllocationUpdate }) => {
          if (processSummaryUpdate) {
            updateAllocationAndSummary(
              allocationEvent.get('userId') != null
                ? {
                    [FetchableResourceType.UserList]: [
                      allocationEvent.get('userId'),
                    ],
                  }
                : {
                    [FetchableResourceType.RoleList]: [
                      allocationEvent.get('roleId'),
                    ],
                  },
              checkRange,
            );
          }

          if (processTotalAllocationUpdate) {
            updateTotalAllocation(
              allocationEvent.get('projectId'),
              allocationEvent.get('userId') != null
                ? {
                    type: ResourceType.User,
                    userId: allocationEvent.get('userId'),
                    roleId: allocationEvent.get('roleId'),
                    referenceTeamId: allocationEvent.get('referenceTeamId'),
                  }
                : {
                    teamId: allocationEvent.get('teamId'),
                    type: ResourceType.Team,
                  },
              differenceInTotalMinutes,
            );
          }
        })
        .finally(() => {
          schedulerRef.current.instance.resumeRefresh(true);
        });
    },
    [
      bulkUpdate,
      schedulerRef,
      updateAllocationAndSummary,
      updateTotalAllocation,
      workingDaysConfig,
      isHoursPerWeek,
    ],
  );
};
